import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSelfTest,
  fetchAdvisorAssessments,
  fetchSelfTests,
  sendAssessment,
  type AdvisorAssessments,
  type SelfTest,
} from '../api/longevityAssessmentApi';

const LONGEVITY_ASSESSMENTS_KEY = ['advisor', 'longevity-assessments'];
const SELF_TESTS_KEY = ['advisor', 'longevity-self-tests'];

/** The advisor's Longevity Assessments section (stat strip + client cards). */
export function useAdvisorAssessments() {
  return useQuery<AdvisorAssessments>({
    queryKey: LONGEVITY_ASSESSMENTS_KEY,
    queryFn: fetchAdvisorAssessments,
  });
}

/** Send (or resend) a longevity assessment link; refreshes the section on success. */
export function useSendAssessment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendAssessment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LONGEVITY_ASSESSMENTS_KEY });
    },
  });
}

/**
 * The advisor's own test runs (Feature 118).
 *
 * Its own query key, not part of the assessments one: the section has to render
 * these for an advisor whose client list is still empty, which is exactly the
 * advisor most likely to be trying the thing out.
 */
export function useSelfTests() {
  return useQuery<SelfTest[]>({
    queryKey: SELF_TESTS_KEY,
    queryFn: fetchSelfTests,
    /*
     * These rows change outside this query's reach. An advisor finishes a run in
     * the public take flow, comes back, and the client's default of a five-minute
     * staleTime with no refetch on focus left the finished run still showing
     * Start/Continue — the one status the advisor just changed themselves.
     *
     * Refetched on mount rather than invalidated from the completion mutation:
     * that mutation belongs to the take flow, which is a different feature and
     * must not know this dashboard's query keys. The list is small and bounded to
     * ten rows, so fetching it whenever the block mounts costs little.
     */
    refetchOnMount: 'always',
  });
}

/** Start a test run. Invalidates only the self-test list — no client data changed. */
export function useCreateSelfTest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSelfTest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SELF_TESTS_KEY });
    },
  });
}
